import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface Scan {
  scan_id: string;
  scan_type: string;
  status: string;
  jurisdiction: string;
  scope: Record<string, any>;
  rule_versions: { rule_id: string; version: number }[];
  lead_fire_count: number;
  leads_created_count: number;
  started_at: string | null;
  finished_at: string | null;
  error_message: string;
  created_at: string;
  dataset_refresh_metadata: any;
}

interface LeadSummary {
  lead_id: string;
  rule_id: string;
  subject_type: string;
  subject_id: string;
  display_name: string;
  amount_usd: string;
  status: string;
}

const ScanDetail = ({ scanId }: { scanId: string }) => {
  const navigate = useNavigate();
  const [scan, setScan] = useState<Scan | null>(null);
  const [leads, setLeads] = useState<LeadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = () => {
      api.get(`/explore/scans/${scanId}/`).then((res) => {
        if (cancelled) return;
        setScan(res.data);
        setLoading(false);
        // If complete, also pull leads
        if (res.data.status === 'complete') {
          api.get(`/explore/scans/${scanId}/leads/`).then((r) => {
            if (!cancelled) setLeads(r.data.leads || []);
          }).catch(() => {});
        }
      }).catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.error || err.message || 'failed');
          setLoading(false);
        }
      });
    };
    fetch();
    // Poll every 3s while pending/running
    const handle = setInterval(() => {
      if (scan && (scan.status === 'pending' || scan.status === 'running')) fetch();
    }, 3000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [scanId, scan?.status]);

  if (loading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !scan) {
    return <Box sx={{ p: 4 }}><Typography color="error">{error || 'Scan not found'}</Typography></Box>;
  }

  // Breadcrumb anchors on the first rule the scan ran. If the scan targeted
  // multiple rules, the Rules section below still lists every one.
  const breadcrumbRuleId = scan.rule_versions?.[0]?.rule_id;

  return (
    <Box>
      <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {breadcrumbRuleId && (
          <Box
            onClick={() => navigate(`/explore/rules/${breadcrumbRuleId}`)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              cursor: 'pointer', mb: 1,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <BackIcon sx={{ fontSize: '1rem' }} />
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {breadcrumbRuleId}
              {scan.rule_versions.length > 1 ? ` +${scan.rule_versions.length - 1} more` : ''}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Scan</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {scan.scan_id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
              <Chip label={scan.status} size="small" color={scanStatusColor(scan.status)} />
              <Chip label={scan.scan_type} size="small" variant="outlined" />
              {scan.jurisdiction && <Chip label={scan.jurisdiction} size="small" variant="outlined" />}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 920 }}>
          <Section title="Stats">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <StatCard label="Lead fires" value={scan.lead_fire_count.toLocaleString()} />
              <StatCard label="New leads" value={scan.leads_created_count.toLocaleString()} />
              <StatCard label="Refreshed" value={(scan.lead_fire_count - scan.leads_created_count).toLocaleString()} />
            </Box>
          </Section>

          <Section title="Rules">
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {scan.rule_versions.map((rv, i) => (
                <Paper
                  key={i}
                  variant="outlined"
                  onClick={() => navigate(`/explore/rules/${rv.rule_id}`)}
                  sx={{
                    px: 2, py: 1, cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(58, 68, 82, 0.05)' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{rv.rule_id}</Typography>
                  <Typography variant="caption" color="text.secondary">v{rv.version}</Typography>
                </Paper>
              ))}
            </Box>
          </Section>

          <Section title="Timing">
            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 0.5, columnGap: 2 }}>
              <Typography variant="body2" color="text.secondary">Created</Typography>
              <Typography variant="body2">{new Date(scan.created_at).toLocaleString()}</Typography>
              <Typography variant="body2" color="text.secondary">Started</Typography>
              <Typography variant="body2">{scan.started_at ? new Date(scan.started_at).toLocaleString() : '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Finished</Typography>
              <Typography variant="body2">{scan.finished_at ? new Date(scan.finished_at).toLocaleString() : '—'}</Typography>
            </Box>
          </Section>

          {scan.status === 'failed' && scan.error_message && (
            <Section title="Error">
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fff5f5' }}>
                <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {scan.error_message}
                </Typography>
              </Paper>
            </Section>
          )}

          {scan.status === 'complete' && (
            <Section title={`Leads (${leads.length})`}>
              {leads.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No leads from this scan.</Typography>
              ) : (
                <Paper variant="outlined">
                  {leads.map((l, i) => (
                    <Box
                      key={l.lead_id}
                      onClick={() => navigate(`/explore/leads/${l.lead_id}`)}
                      sx={{
                        px: 2, py: 1.5,
                        borderBottom: i < leads.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {l.display_name || `${l.subject_type}:${l.subject_id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {l.rule_id} · ${parseFloat(l.amount_usd || '0').toLocaleString()}
                          </Typography>
                        </Box>
                        <Chip label={l.status} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Box>
                    </Box>
                  ))}
                </Paper>
              )}
            </Section>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const StatCard = ({ label, value }: { label: string; value: string }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>{value}</Typography>
  </Paper>
);

function scanStatusColor(s: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (s) {
    case 'complete': return 'success';
    case 'running': return 'info';
    case 'pending': return 'warning';
    case 'failed': return 'error';
    default: return 'default';
  }
}

export default ScanDetail;
